"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Mail, Lock, Loader2, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const router = useRouter();
  
  // Login form states (Ab mobile ki jagah email use hoga)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [loginError, setLoginError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    setSessionConflict(false);

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.ok) {
        router.push("/"); 
      } else {
        const data = await res.json();
        setLoginError(data.message || "Invalid email or password");
        setSessionConflict(res.status === 409);
      }
    } catch (error) {
      setLoginError("Something went wrong during login.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    if (!email || !password || logoutAllLoading) return;
    if (!window.confirm("Logout this account from all devices?")) return;

    setLogoutAllLoading(true);
    setLoginError("");

    try {
      const res = await fetch("/api/logout-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setLoginError(data.message || "Unable to logout devices");
        return;
      }

      setSessionConflict(false);
      setLoginError("All devices logged out. You can now login again.");
    } catch {
      setLoginError("Unable to logout devices. Please try again.");
    } finally {
      setLogoutAllLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        
        {/* Header */}
        <div className="text-center">
          <Link href="/" className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Welcome Back</h2>
          <p className="mt-2 text-sm text-gray-600">Login to your ApnaSaathi account</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            
            {/* Email Input */}
            <div className="relative">
              <Mail className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Email Address" 
              />
            </div>

            {/* Password Input & Forgot Password Link */}
            <div>
              <div className="relative">
                <Lock className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <input 
                  type="password" 
                  required 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  placeholder="Password" 
                />
              </div>
              
              <div className="flex justify-end mt-2">
                <Link href="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500 font-medium transition">
                  Forgot Password?
                </Link>
              </div>
            </div>

          </div>

          {/* Login Button */}
          <button 
            type="submit" 
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 transition"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Login"}
          </button>
        </form>

        {loginError && (
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p>{loginError}</p>
            {sessionConflict && (
              <button
                type="button"
                onClick={() => void handleLogoutAll()}
                disabled={loading || logoutAllLoading}
                className="mt-3 w-full rounded-lg bg-red-600 px-3 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {logoutAllLoading ? "Logging out devices..." : "Logout All Devices"}
              </button>
            )}
          </div>
        )}

        {/* Register Link */}
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Don't have an account?{" "}
            <Link href="/register" className="font-medium text-blue-600 hover:text-blue-500">
              Register here
            </Link>
          </p>
        </div>

        {/* Step4499_LOGIN_LEGAL_LINKS */}
        <div className="border-t border-gray-200 pt-4 text-center text-sm text-gray-600">
          <p>Read our development-draft policies:</p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Link
              href="/privacy-policy"
              className="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-and-conditions"
              className="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Terms &amp; Conditions
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}